import { ref, reactive } from 'vue';
import apiClient from '@/api/client';
import { message } from 'ant-design-vue';
import type { LeadsListQuery, UpdateLeadRequest, CreateNoteRequest } from '@hyperke/shared';
import { featureFlags } from '@/feature-flags';

/**
 * Lead interface
 */
export interface Lead {
  id: string;
  lead_id: number;
  campaign_id: number;
  campaign_name: string | null;
  campaign_status: string | null;
  client_code: string | null;
  to_email: string | null;
  to_name: string | null;
  to_name_override: string | null; // Manual name override
  phone_number: string | null;
  phone_override: string | null; // Manual phone number override
  effective_phone: string | null; // Effective phone (override takes precedence)
  company_name: string | null;
  company_name_override: string | null; // Manual company name override
  website: string | null;
  website_override: string | null; // Manual website override
  current_category_name: string | null;
  sdr_status: string;
  sdr_last_contacted_at: string | null;
  sdr_first_contacted_at: string | null;
  im_comment: string | null;
  sdr_comment: string | null;
  // Phase 2: Additional workflow fields
  sdr_owner_user_id: string | null;
  priority: string | null;
  next_action: string | null;
  due_at: string | null;
  last_interested_at: string;
  last_reply_time: string | null;
  appointment_booked_at: string | null; // Timestamp when lead converted from Interested → Appointment Booked
  meeting_date: string | null; // Meeting date (date only)
  meeting_time: string | null; // Meeting time in HH:mm format (24-hour)
  meeting_timezone: string | null; // IANA timezone identifier
  [key: string]: any;
}

/**
 * Leads list response
 */
export interface LeadsListResponse {
  data: Lead[];
  pagination: {
    page: number;
    page_size: number;
    total: number;
    total_pages: number;
  };
}

/**
 * Lead detail response
 */
export interface LeadDetailResponse {
  lead: Lead;
  transitions: Array<{
    id: string;
    event_timestamp: string;
    old_category_name: string | null;
    new_category_name: string | null;
  }>;
  notes: Array<{
    id: string;
    author_role: string;
    note_text: string;
    created_at: string;
  }>;
}

/**
 * Leads composable
 * Provides functions for fetching and updating leads
 * 
 * @returns Leads composable functions and state
 */
export function useLeads() {
  const loading = ref(false);
  const error = ref<string | null>(null);

  /**
   * Fetch leads list
   *
   * @param query - Query parameters (filters, pagination, sorting)
   * @returns Promise<LeadsListResponse>
   */

  async function fetchLeads(query: Partial<LeadsListQuery>): Promise<LeadsListResponse> {
    loading.value = true;
    error.value = null;

    try {
      // Check if unified leads feature is enabled
      // When enabled, ALWAYS use unified leads API (works with or without clientCode)
      const clientCode = query.client_code;
      const useUnified = featureFlags.unifiedLeads?.enabled;

      console.log('[useLeads] fetchLeads called:', {
        clientCode,
        useUnified,
        featureFlagEnabled: featureFlags.unifiedLeads?.enabled,
        unifiedLeadsEnabled: featureFlags.unifiedLeads?.enabled
      });

      if (useUnified) {
        // Use unified leads endpoint (supports both with and without clientCode)
        console.log('[useLeads] Using UNIFIED API', clientCode ? `for client: ${clientCode}` : '(all clients)');
        // If clientCode exists, use /leads/unified/:clientCode, otherwise use /leads/unified
        const endpoint = clientCode ? `/leads/unified/${clientCode}` : '/leads/unified';

        // Map frontend filters to what the unified API understands
        // The unified API accepts: category_id (numeric) or category_name (string pattern)
        let categoryId: number | undefined;
        let categoryName: string | undefined;

        if (query.current_category_name) {
          // Appointments page sends this directly - use as category_name
          categoryName = query.current_category_name;
        } else if (query.has_transitioned_to === 'interested') {
          // Leads page sends has_transitioned_to='interested' - map to category name
          categoryName = 'Interested%';
        } else if (query.categoryId !== undefined) {
          // Fallback to numeric category_id if provided
          categoryId = query.categoryId;
        }

        console.log('[useLeads] Unified API filters:', { categoryId, categoryName, has_transitioned_to: query.has_transitioned_to, current_category_name: query.current_category_name, date_from: query.date_from, date_to: query.date_to });

        const apiParams = {
          page: query.page,
          page_size: query.pageSize,
          category_id: categoryId,
          category_name: categoryName,
          // Date filters - explicitly log each one
          date_from: query.date_from,
          date_to: query.date_to,
          meeting_date_from: query.meeting_date_from,
          meeting_date_to: query.meeting_date_to,
          // Search filter - search across email, name, company
          search: query.search,
          // Additional filters for unified API
          call_feedback: query.call_feedback,
          sdr_status: query.sdr_status,
          client_code: query.client_code,
          assigned_sdr_id: query.assigned_sdr_id,
          assigned_inbox_manager_id: query.assigned_inbox_manager_id,
          assigned_account_manager_id: query.assigned_account_manager_id
        };
        console.log('[useLeads] API REQUEST:', endpoint, 'params:', apiParams);
        const response = await apiClient.get(endpoint, { params: apiParams });

        // Transform unified response to legacy format
        const unifiedData = response.data;
        const transformedLeads = unifiedData.data.map((lead: any) => ({
          id: lead.id,
          lead_id: lead.numeric_lead_id || lead.campaigns?.[0]?.lead_id || 0,
          campaign_id: lead.campaigns?.[0]?.campaign_id || 0,
          campaign_name: lead.campaigns?.[0]?.campaign_name || null,
          campaign_status: lead.campaigns?.[0]?.campaign_status || null,
          client_code: lead.client_code,
          to_email: lead.email,
          to_name: lead.to_name || null,
          to_name_override: null,
          phone_number: lead.phone_number || null,
          phone_override: null,
          effective_phone: lead.phone_number || null,
          company_name: lead.company_name || null,
          company_name_override: null,
          website: lead.website || null,
          website_override: null,
          current_category_name: lead.current_category_name,
          sdr_status: lead.sdr_status || 'NEW',
          sdr_last_contacted_at: lead.sdr_last_contacted_at,
          sdr_first_contacted_at: lead.first_interested_at,
          im_comment: lead.im_comment || null,
          sdr_comment: lead.sdr_comment || null,
          call_feedback: lead.call_feedback || null,
          sdr_owner_user_id: lead.assigned_sdr_id,
          priority: null,
          next_action: null,
          due_at: null,
          last_interested_at: lead.current_category_date,
          last_reply_time: lead.last_reply_time,
          appointment_booked_at: lead.appointment_booked_at || (lead.current_category_name === 'Appointment Booked' ? lead.current_category_date : null),
          meeting_date: lead.meeting_date || lead.latest_meeting_date || null,
          meeting_time: lead.meeting_time || null,
          meeting_timezone: lead.meeting_timezone || null,
          // Assignment fields (IDs)
          assigned_inbox_manager_id: lead.assigned_im_id,
          assigned_sdr_id: lead.assigned_sdr_id,
          assigned_account_manager_id: lead.assigned_am_id,
          // Assignment names - use actual names from API (joined from app_users)
          assigned_im_name: lead.assigned_im_name || null,
          assigned_im_email: lead.assigned_im_email || null,
          assigned_sdr_name: lead.assigned_sdr_name || null,
          assigned_sdr_email: lead.assigned_sdr_email || null,
          assigned_am_name: lead.assigned_am_name || null,
          assigned_am_email: lead.assigned_am_email || null,
          // From email
          from_email: lead.from_email || null,
          // Master inbox URL (from unified leads)
          app_url: lead.app_url || null,
          // Unified-specific fields
          _unified: true,
          campaigns: lead.campaigns,  // For expand rendering
          _campaigns: lead.campaigns,
          _campaign_count: lead.campaigns?.length || 0
        }));

        return {
          data: transformedLeads,
          pagination: unifiedData.pagination
        };
      }

      // Legacy endpoint
      console.log('[useLeads] Using LEGACY API - clientCode:', clientCode, 'useUnified:', useUnified);
      const response = await apiClient.get<LeadsListResponse>('/leads', { params: query });
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch leads';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch lead detail
   * 
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @returns Promise<LeadDetailResponse>
   */
  async function fetchLeadDetail(leadId: number, campaignId: number): Promise<LeadDetailResponse> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.get<LeadDetailResponse>(
        `/leads/${leadId}/${campaignId}`
      );
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch lead detail';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update lead
   * 
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @param updates - Fields to update (only workflow fields)
   * @returns Promise<Lead>
   */
  async function updateLead(
    leadId: number,
    campaignId: number,
    updates: UpdateLeadRequest
  ): Promise<Lead> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.patch<{ lead: Lead }>(
        `/leads/${leadId}/${campaignId}`,
        updates
      );
      // Note: Success toast is shown by updateLeadOptimistic (caller) to avoid duplicate messages
      return response.data.lead;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update lead';
      message.error(error.value);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Create note for lead
   * 
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @param noteText - Note text
   * @returns Promise<{ id: string; created_at: string }>
   */
  async function addNote(
    leadId: number,
    campaignId: number,
    noteText: string
  ): Promise<{ id: string; created_at: string }> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.post<{ note: { id: string; created_at: string } }>(
        `/leads/${leadId}/${campaignId}/notes`,
        { note_text: noteText }
      );
      message.success('Note added successfully');
      return response.data.note;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to add note';
      message.error(error.value);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update lead phone override
   *
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @param phoneOverride - New phone override (null to clear)
   * @returns Promise<Lead>
   */
  async function updatePhoneOverride(
    leadId: number,
    campaignId: number,
    phoneOverride: string | null
  ): Promise<Lead> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.patch<{ lead: Lead }>(
        `/leads/${leadId}/${campaignId}`,
        { phone_override: phoneOverride }
      );
      message.success('Phone number updated successfully');
      return response.data.lead;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update phone number';
      message.error(error.value);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update lead name override
   *
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @param nameOverride - New name override (null to clear)
   * @returns Promise<Lead>
   */
  async function updateNameOverride(
    leadId: number,
    campaignId: number,
    nameOverride: string | null
  ): Promise<Lead> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.patch<{ lead: Lead }>(
        `/leads/${leadId}/${campaignId}`,
        { to_name_override: nameOverride }
      );
      message.success('Name updated successfully');
      return response.data.lead;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update name';
      message.error(error.value);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update lead company name override
   *
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @param companyOverride - New company name override (null to clear)
   * @returns Promise<Lead>
   */
  async function updateCompanyOverride(
    leadId: number,
    campaignId: number,
    companyOverride: string | null
  ): Promise<Lead> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.patch<{ lead: Lead }>(
        `/leads/${leadId}/${campaignId}`,
        { company_name_override: companyOverride }
      );
      message.success('Company name updated successfully');
      return response.data.lead;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update company name';
      message.error(error.value);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Update lead website override
   *
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @param websiteOverride - New website override (null to clear)
   * @returns Promise<Lead>
   */
  async function updateWebsiteOverride(
    leadId: number,
    campaignId: number,
    websiteOverride: string | null
  ): Promise<Lead> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.patch<{ lead: Lead }>(
        `/leads/${leadId}/${campaignId}`,
        { website_override: websiteOverride }
      );
      message.success('Website updated successfully');
      return response.data.lead;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update website';
      message.error(error.value);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch field audit history for a lead
   *
   * @param leadId - Lead ID
   * @param campaignId - Campaign ID
   * @param fieldName - Field name (default: 'phone_override')
   * @returns Promise<Audit history>
   */
  async function fetchFieldAuditHistory(
    leadId: number,
    campaignId: number,
    fieldName: string = 'phone_override'
  ): Promise<{
    field_name: string;
    audit_history: Array<{
      id: string;
      lead_id: number;
      campaign_id: number;
      field_name: string;
      old_value: string | null;
      new_value: string | null;
      updated_by_user_id: string;
      updated_by_role: string;
      updated_by_email: string | null;
      change_source: string;
      trace_id: string | null;
      updated_at: string | null;
    }>;
    total_count: number;
  }> {
    loading.value = true;
    error.value = null;

    try {
      const response = await apiClient.get<{
        field_name: string;
        audit_history: any[];
        total_count: number;
      }>(
        `/leads/${leadId}/${campaignId}/audit-history`,
        { params: { field_name: fieldName } }
      );
      return response.data;
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch audit history';
      throw err;
    } finally {
      loading.value = false;
    }
  }

  /**
   * Fetch KPI counts (exact totals matching current filters)
   *
   * @param query - Query parameters (same filters as leads list)
   * @returns Promise<{ total: number; new: number; unassigned: number; unassigned_sdr: number; unassigned_im: number; unassigned_am: number; today: number }>
   */
  async function fetchKPIs(query: Partial<LeadsListQuery>, signal?: AbortSignal): Promise<{ total: number; new: number; unassigned: number; unassigned_sdr: number; unassigned_im: number; unassigned_am: number; today: number }> {
    try {
      const response = await apiClient.get<{ total: number; new: number; unassigned: number; unassigned_sdr: number; unassigned_im: number; unassigned_am: number; today: number }>('/leads/kpis', { 
        params: query,
        signal, // Support request cancellation
      });
      return response.data;
    } catch (err: any) {
      // Don't log abort errors (expected when cancelling)
      if (err.name !== 'AbortError' && err.code !== 'ERR_CANCELED') {
        console.error('Failed to fetch KPIs:', err);
      }
      // Return fallback values on error
      return { total: 0, new: 0, unassigned: 0, unassigned_sdr: 0, unassigned_im: 0, unassigned_am: 0, today: 0 };
    }
  }

  return {
    loading,
    error,
    fetchLeads,
    fetchLeadDetail,
    updateLead,
    updatePhoneOverride,
    updateNameOverride,
    updateCompanyOverride,
    updateWebsiteOverride,
    fetchFieldAuditHistory,
    addNote,
    fetchKPIs,
  };
}